import React from 'react';
import { FadeIn } from '../ui/FadeIn';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiX, FiStar } = FiIcons;

const rows = [
  { feature: 'YouTube URL input', vr: true, p: true, oc: true, d: false, l: true },
  { feature: 'Auto-generate slides', vr: true, p: false, oc: false, d: false, l: false },
  { feature: 'Rebuild narration (AI)', vr: true, p: false, oc: false, d: false, l: false },
  { feature: 'Custom TTS voice', vr: true, p: false, oc: false, d: 'Clone', l: false },
  { feature: 'Educational structure', vr: true, p: false, oc: false, d: false, l: false },
  { feature: 'Motion video (AI)', vr: true, p: false, oc: false, d: false, l: false },
  { feature: 'Export PDF/PPTX', vr: true, p: false, oc: false, d: false, l: false },
  { feature: 'White-label', vr: true, p: 'Ent.', oc: false, d: false, l: 'Ent.' },
  { feature: 'API access', vr: true, p: 'Ent.', oc: false, d: 'Pro', l: 'Ent.' },
];

const RenderValue = ({ val, isHighlight }) => {
  if (val === true) return <SafeIcon icon={FiCheck} className={`text-2xl mx-auto ${isHighlight ? 'text-brand-electric' : 'text-white/60'}`} />;
  if (val === false) return <SafeIcon icon={FiX} className="text-2xl mx-auto text-white/20" />;
  return <span className={`text-sm font-bold ${isHighlight ? 'text-white' : 'text-white/60'}`}>{val}</span>;
}

const ComparisonTable = () => {
  return (
    <section className="py-28 bg-gradient-to-b from-brand-dark via-brand-card/10 to-brand-dark relative border-t border-white/5">
      {/* Decorative separator */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-neon/50 to-transparent"></div>
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <FadeIn className="text-center mb-12 md:mb-20">
          <h2 className="text-3xl md:text-6xl font-black mb-6 text-white">The only platform that does all of it</h2>
          <p className="text-lg md:text-xl text-white/70 max-w-2xl mx-auto">
            See how VidRevive stacks up against the competition
          </p>
        </FadeIn>

        <FadeIn>
          {/* Desktop Table */}
          <div className="hidden md:block glass-card overflow-x-auto border border-white/10 shadow-2xl">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="p-6 text-white/80 font-bold text-base w-1/3">Feature</th>
                  <th className="p-6 text-center font-black text-white bg-gradient-to-b from-brand-electric/20 to-brand-electric/10 border-x border-brand-electric/40 relative min-w-[140px]">
                    <div className="flex flex-col items-center gap-2">
                      <span className="text-lg">VidRevive</span>
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <SafeIcon key={i} icon={FiStar} className="text-brand-gold text-sm fill-current" />
                        ))}
                      </div>
                      <span className="text-xs text-brand-electric font-bold bg-brand-electric/20 px-3 py-1 rounded-full border border-brand-electric/40">
                        ⭐ Best Choice
                      </span>
                    </div>
                  </th>
                  <th className="p-6 text-center text-white/70 font-bold min-w-[120px]">Pictory</th>
                  <th className="p-6 text-center text-white/70 font-bold min-w-[120px]">Opus Clip</th>
                  <th className="p-6 text-center text-white/70 font-bold min-w-[120px]">Descript</th>
                  <th className="p-6 text-center text-white/70 font-bold min-w-[120px]">Lumen5</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={idx} className="border-t border-white/5 hover:bg-white/5 transition-colors">
                    <td className="p-6 text-base text-white font-medium">{row.feature}</td>
                    <td className="p-6 text-center bg-brand-electric/5 border-x border-brand-electric/40">
                      <RenderValue val={row.vr} isHighlight />
                    </td>
                    <td className="p-6 text-center"><RenderValue val={row.p} /></td>
                    <td className="p-6 text-center"><RenderValue val={row.oc} /></td>
                    <td className="p-6 text-center"><RenderValue val={row.d} /></td>
                    <td className="p-6 text-center"><RenderValue val={row.l} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-6">
            {/* Best Choice Badge */}
            <div className="text-center mb-8">
              <div className="inline-flex flex-col items-center gap-2 bg-gradient-to-r from-brand-neon/20 to-brand-electric/20 text-white px-6 py-4 rounded-2xl border-2 border-brand-electric/40 shadow-lg shadow-brand-electric/30">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <SafeIcon key={i} icon={FiStar} className="text-brand-gold text-base fill-current" />
                  ))}
                </div>
                <span className="text-sm font-black uppercase tracking-wider">⭐ VidRevive - Best Choice</span>
              </div>
            </div>

            {rows.map((row, idx) => (
              <div key={idx} className="glass-card p-6 border border-white/10">
                <h4 className="text-white font-bold mb-4 text-lg">{row.feature}</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-brand-electric/10 rounded-lg border border-brand-electric/40">
                    <div className="text-xs text-brand-electric font-bold mb-2">VidRevive</div>
                    <RenderValue val={row.vr} isHighlight />
                  </div>
                  <div className="text-center p-3 bg-white/5 rounded-lg border border-white/10">
                    <div className="text-xs text-white/60 font-bold mb-2">Pictory</div>
                    <RenderValue val={row.p} />
                  </div>
                  <div className="text-center p-3 bg-white/5 rounded-lg border border-white/10">
                    <div className="text-xs text-white/60 font-bold mb-2">Opus Clip</div>
                    <RenderValue val={row.oc} />
                  </div>
                  <div className="text-center p-3 bg-white/5 rounded-lg border border-white/10">
                    <div className="text-xs text-white/60 font-bold mb-2">Descript</div>
                    <RenderValue val={row.d} />
                  </div>
                  <div className="text-center p-3 bg-white/5 rounded-lg border border-white/10 col-span-2">
                    <div className="text-xs text-white/60 font-bold mb-2">Lumen5</div>
                    <RenderValue val={row.l} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </FadeIn>

      </div>
    </section>
  );
};

export default ComparisonTable;